import { createContext, useCallback, useContext, useMemo } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { DrawerName, useDrawersStore } from 'src/stores/useDrawersStore';

interface SideDrawerContextType {
	/**
	 * Closes the side drawer with a transition and invokes passed `onClose` callback.
	 */
	close: () => void;
}

const SideDrawerContext = createContext<SideDrawerContextType | null>(null);

interface Props {
	onClose?: () => void;
	drawerName: DrawerName;
	children: React.ReactNode;
}

export default function SideDrawerProvider({
	children,
	drawerName,
	onClose,
}: Props) {
	const handleCloseDrawer = useDrawersStore((s) => s.handleCloseDrawer);
	const onCloseRef = useSyncedRef(onClose);

	const close = useCallback(() => {
		handleCloseDrawer(drawerName);

		onCloseRef.current?.();
	}, [drawerName, handleCloseDrawer, onCloseRef]);

	const value = useMemo(() => ({ close }), [close]);

	return (
		<SideDrawerContext.Provider value={value}>
			{children}
		</SideDrawerContext.Provider>
	);
}

export const useSideDrawer = () => {
	const context = useContext(SideDrawerContext);

	if (!context) {
		throw new Error('useSideDrawer must be used within a SideDrawerProvider');
	}

	return context;
};
